import { useEffect, useState, type FormEvent } from "react"
import { Send01 } from "@untitledui/icons"
import { toast } from "sonner"
import { teamsApi } from "@/lib/api/teams"
import { ApiError } from "@/lib/api/client"
import { useQueryClient } from "@tanstack/react-query"
import { queryKeys } from "@/lib/api/query-keys"
import { DesignDialog } from "@/components/ui/design-dialog"
import { DesignSelect } from "@/components/ui/design-select"
import { US_STATES } from "@/components/ui/us-state-select"
import {
  Field,
  onboardingInputClass,
  onboardingInputErrorClass,
} from "@/components/onboarding/onboarding-shell"
import { cx } from "@/lib/utils/cx"

// First invite during the create-flow is always the GM by definition —
// no role picker needed. Other roles get invited later from
// Settings → {dealership} → Members & invites.

interface AddDealershipDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  groupTeamId: number | null
  groupName: string | null
}

export function AddDealershipDialog({
  open,
  onOpenChange,
  groupTeamId,
  groupName,
}: AddDealershipDialogProps) {
  const queryClient = useQueryClient()

  const [name, setName] = useState("")
  const [city, setCity] = useState("")
  const [state, setState] = useState("")
  const [gmEmail, setGmEmail] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState<{
    name?: string
    city?: string
    state?: string
    gmEmail?: string
  }>({})

  useEffect(() => {
    if (open) {
      setName("")
      setCity("")
      setState("")
      setGmEmail("")
      setErrors({})
    }
  }, [open])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (groupTeamId == null) return

    const next: typeof errors = {}
    if (!name.trim()) next.name = "Name is required"
    if (!city.trim()) next.city = "City is required"
    if (!state.trim()) next.state = "Pick a state"
    if (!gmEmail.trim()) {
      next.gmEmail = "GM email is required so they can access the dealership"
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(gmEmail.trim())) {
      next.gmEmail = "Enter a valid email"
    }
    if (Object.keys(next).length > 0) {
      setErrors(next)
      return
    }

    setSubmitting(true)
    try {
      // BE creates the dealership, auto-creates the GM user, and stamps the
      // membership/invite atomically when generalManagerEmail is sent in
      // this single call. A follow-up teamsApi.invite would be redundant
      // (and the BE rejects the create without the email).
      await teamsApi.addDealership(groupTeamId, {
        dealershipName: name.trim(),
        city: city.trim(),
        state: state.trim(),
        generalManagerEmail: gmEmail.trim(),
      })
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.all })
      toast.success(`${name.trim()} added · GM invite sent`)
      onOpenChange(false)
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.message : "Couldn't add this dealership"
      toast.error(msg)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <DesignDialog.Root open={open} onOpenChange={onOpenChange}>
      <DesignDialog.Content width="md">
        <DesignDialog.Header>
          <DesignDialog.Title>Add a dealership</DesignDialog.Title>
          <DesignDialog.Description>
            New dealership under{" "}
            <span className="font-semibold text-zinc-700">
              {groupName ?? "your group"}
            </span>
            . The GM gets the first invite so they can run it day-to-day.
          </DesignDialog.Description>
        </DesignDialog.Header>
        <form onSubmit={handleSubmit}>
          <DesignDialog.Body className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-[1.4fr_1fr] gap-3">
              <Field label="Dealership name" required error={errors.name}>
                <input
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value)
                    if (errors.name) setErrors((er) => ({ ...er, name: undefined }))
                  }}
                  placeholder="e.g. Austin Honda"
                  maxLength={255}
                  className={cx(onboardingInputClass, errors.name && onboardingInputErrorClass)}
                  autoFocus
                />
              </Field>
              <Field label="City" required error={errors.city}>
                <input
                  value={city}
                  onChange={(e) => {
                    setCity(e.target.value)
                    if (errors.city) setErrors((er) => ({ ...er, city: undefined }))
                  }}
                  placeholder="e.g. Austin"
                  maxLength={120}
                  className={cx(onboardingInputClass, errors.city && onboardingInputErrorClass)}
                />
              </Field>
            </div>
            <Field label="State" required error={errors.state}>
              <DesignSelect.Root
                value={state}
                onValueChange={(v) => {
                  setState(v)
                  if (errors.state) setErrors((er) => ({ ...er, state: undefined }))
                }}
              >
                <DesignSelect.Trigger
                  className={cx(
                    onboardingInputClass,
                    "text-left flex items-center justify-between",
                    !state && "text-zinc-400",
                    errors.state && onboardingInputErrorClass,
                  )}
                >
                  <DesignSelect.Value placeholder="Pick a state" />
                </DesignSelect.Trigger>
                <DesignSelect.Content>
                  {US_STATES.map((s) => (
                    <DesignSelect.Item key={s} value={s}>
                      {s}
                    </DesignSelect.Item>
                  ))}
                </DesignSelect.Content>
              </DesignSelect.Root>
            </Field>
            <Field
              label="GM email"
              required
              hint="They'll be invited as General Manager"
              error={errors.gmEmail}
            >
              <input
                type="email"
                value={gmEmail}
                onChange={(e) => {
                  setGmEmail(e.target.value)
                  if (errors.gmEmail) setErrors((er) => ({ ...er, gmEmail: undefined }))
                }}
                placeholder="manager@dealership.com"
                className={cx(onboardingInputClass, errors.gmEmail && onboardingInputErrorClass)}
              />
            </Field>
          </DesignDialog.Body>
          <DesignDialog.Footer>
            <DesignDialog.Close asChild>
              <button
                type="button"
                className="h-12 px-5 rounded-xl text-[14px] font-semibold text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 transition-all duration-150"
              >
                Cancel
              </button>
            </DesignDialog.Close>
            <button
              type="submit"
              disabled={submitting || groupTeamId == null}
              className="h-12 px-7 inline-flex items-center gap-2 rounded-xl bg-zinc-900 text-white text-[15px] font-semibold shadow-lg shadow-zinc-900/20 hover:bg-black hover:shadow-zinc-900/30 transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
            >
              <Send01 className="size-4" aria-hidden />
              {submitting ? "Adding…" : "Add dealership & invite GM"}
            </button>
          </DesignDialog.Footer>
        </form>
      </DesignDialog.Content>
    </DesignDialog.Root>
  )
}
